import React, { useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  Container,
  Grid,
} from "@mui/material";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LoginIcon from "@mui/icons-material/Login";
import Logo from "../../Assets/Images/loginlogo.svg";

const LandingLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Scroll to top on route change
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname]);

  const scrollToElement = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div>
      {/* HEADER */}
      <AppBar
        position="sticky"
        sx={{
          top: 0,
          background: "#fff",
          boxShadow: "none",
          zIndex: 10,
          height: "auto",
          padding: "0px 60px",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            padding: "0px 5%",
            width: "100%",
            minHeight: { xs: 60, sm: 70, md: 80, lg: 90 },
            paddingY: { xs: 0.5, sm: 1 },
          }}
        >
          {/* Logo */}
          <Typography
            variant="h6"
            sx={{
              color: "#228FD8",
              fontWeight: 600,
              display: "flex",
              zIndex: 2,
            }}
          >
            <img
              src={Logo}
              alt="Logo"
              height="55"
              style={{
                height: "56px",
                maxHeight: "64px",
                width: "auto",
                marginTop: "8px",
                cursor: "pointer",
                display: "block",
              }}
              onClick={() => {
                navigate("/home");
              }}
            />
          </Typography>

          {/* Desktop Links */}
          <div style={{ display: "flex", gap: "10px" }}>
            <Box sx={{ display: { xs: "none", md: "flex" }, gap: 3 }}>
              <Button
                sx={{
                  fontWeight: 600,
                  fontSize: "15px",
                  color: "#374151",
                  textTransform: "capitalize",
                  transition: "all 0.3s ease",
                  "&:hover": { transform: "translateY(-5px)" },
                }}
                onClick={() => scrollToElement("how-it-works")}
              >
                How It Works
              </Button>
              <Button
                sx={{
                  fontWeight: 600,
                  fontSize: "15px",
                  color: "#374151",
                  textTransform: "capitalize",
                  transition: "all 0.3s ease",
                  "&:hover": { transform: "translateY(-5px)" },
                }}
                onClick={() => scrollToElement("Pricing")}
              >
                Pricing
              </Button>
              <Button
                onClick={() => navigate("/login")}
                sx={{
                  fontWeight: 600,
                  color: "#374151",
                  fontSize: "12px",
                  textTransform: "capitalize",
                  transition: "all 0.3s ease",
                  "&:hover": { transform: "translateY(-5px)" },
                }}
              >
                <LoginIcon sx={{ marginRight: "10px" }} /> Log in
              </Button>
            </Box>

            {/* CTA Button */}
            <Button
              variant="contained"
              sx={{
                display: { xs: "none", md: "flex" },
                backgroundColor: "#009DFF",
                color: "#fff",
                borderRadius: "12px",
                fontWeight: 600,
                padding: "12px 24px",
                fontSize: "15px",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  backgroundColor: "#009DFF",
                },
                textTransform: "capitalize",
              }}
              onClick={() => scrollToElement("Pricing")}
            >
              Start For Free
            </Button>
          </div>

          {/* Mobile Menu Icon */}
          <IconButton
            sx={{
              display: { xs: "flex", md: "none" },
              color: "black",
              zIndex: 3,
            }}
            onClick={() => setMobileMenuOpen(true)}
            aria-label="open menu"
          >
            <MenuIcon />
          </IconButton>

          {/* Mobile Drawer */}
          <Drawer
            anchor="top"
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
            PaperProps={{
              sx: {
                position: "absolute",
                left: 0,
                right: 0,
                width: "100%",
                background: "#fff",
                boxShadow: 3,
                height: "auto",
                maxWidth: "100vw",
                maxHeight: "90vh",
                overflow: "visible",
                flexDirection: "column",
                p: 0,
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
                margin: 0,
              },
            }}
          >
            {/* Top Row: Logo + Close Icon */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 2,
                borderBottom: "1px solid #f0f0f0",
                width: "100%",
              }}
            >
              <img
                src={Logo}
                alt="Logo"
                style={{ height: 38, cursor: "pointer" }}
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate("/");
                }}
              />
              <IconButton onClick={() => setMobileMenuOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Menu Items */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                px: 2,
                py: 2,
                width: "100%",
                alignItems: "flex-start",
              }}
            >
              <Button
                fullWidth
                onClick={() => {
                  setMobileMenuOpen(false);
                  scrollToElement("how-it-works");
                }}
                sx={{
                  justifyContent: "flex-start",
                  color: "#374151",
                  fontWeight: 600,
                  fontSize: "16px",
                  textTransform: "capitalize",
                }}
              >
                How It Works
              </Button>
              <Button
                fullWidth
                onClick={() => {
                  setMobileMenuOpen(false);
                  scrollToElement("Pricing");
                }}
                sx={{
                  justifyContent: "flex-start",
                  color: "#374151",
                  fontWeight: 600,
                  fontSize: "16px",
                  textTransform: "capitalize",
                }}
              >
                Pricing
              </Button>
              <Button
                fullWidth
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate("/login");
                }}
                sx={{
                  justifyContent: "flex-start",
                  color: "#374151",
                  fontWeight: 600,
                  fontSize: "16px",
                  textTransform: "capitalize",
                }}
              >
                <LoginIcon sx={{ marginRight: "10px" }} /> Log in
              </Button>
              <Button
                fullWidth
                variant="contained"
                sx={{
                  mt: 2,
                  backgroundColor: "#009DFF",
                  color: "#fff",
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "15px",
                  textTransform: "capitalize",
                }}
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate("/login", { state: { path: "minimock" } });
                }}
              >
                Start For Free
              </Button>
            </Box>
          </Drawer>
        </Toolbar>
      </AppBar>

      {/* ROUTE CONTENT */}
       <Box
          className="layout-content">
          <Outlet />
        </Box>

      {/* FOOTER */}
      <Box
        sx={{
          backgroundColor: "#111827",
          color: "white",
          py: 6,
          px:4,
        }}
      >
        <Container maxWidth="lg" sx={{px:4, width:"90%"}}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h4"
                sx={{ fontWeight: "500", mb: 2, fontSize: "18px" }}
              >
                Everest GATE AI Online Question Bank
              </Typography>
              <Typography
                variant="body2"
                sx={{ mb: 2, fontSize: "15px", color: "#D1D5DB" }}
              >
                The AI-powered GATE exam preparation platform built by 99+ ATAR WA Tutors
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography
                variant="h5"
                sx={{ fontWeight: "500", mb: 2, fontSize: "18px" }}
              >
                Quick Links
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontSize: "14px",
                  color: "#D1D5DB",
                  cursor: "pointer",
                  "&:hover": { color: "#009dff" },
                }}
                onClick={() => scrollToElement("how-it-works")}
              >
                How it Works
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontSize: "14px",
                  color: "#D1D5DB",
                  cursor: "pointer",
                  "&:hover": { color: "#009dff" },
                }}
                onClick={() => scrollToElement("Pricing")}
              >
                Pricing
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography
                variant="h5"
                sx={{ fontWeight: "500", mb: 2, fontSize: "18px" }}
              >
                Contact
              </Typography>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontSize: "15px", color: "#D1D5DB" }}
              >
                3/723 Ranford Road, Harrisdale, WA 6155, Australia
              </Typography>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontSize: "15px", color: "#D1D5DB" }}
              >
                info@everesttutoring.com.au
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "15px", color: "#D1D5DB" }}>
                +61 404 604 673
              </Typography>
            </Grid>
          </Grid>

          <Box
            sx={{
              borderTop: "1px solid rgba(255, 255, 255, 0.1)",
              mt: 4,
              pt: 4,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: { xs: "center", sm: "space-between" },
              alignItems: "center",
              gap: 2,
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            <Typography variant="body2" sx={{ fontSize: "15px", color: "#D1D5DB" }}>
              © 2025 Everest Tutoring. All rights reserved.
            </Typography>
            <Box sx={{ display: "flex", gap: 3 }}>
              <Typography
                variant="body2"
                onClick={() => navigate("/privacypolicy")}
                sx={{
                  fontSize: "15px",
                  color: "#D1D5DB",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Privacy Policy
              </Typography>
              <Typography
                variant="body2"
                onClick={() => navigate("/termsandservices")}
                sx={{
                  fontSize: "15px",
                  color: "#D1D5DB",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Terms of Service
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </div>
  );
};

export default LandingLayout;
